import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Store } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

import { api } from "@/api";
import {
  Button,
  EmptyState,
  ErrorState,
  HorsePortrait,
  money,
  Panel,
  SectionHeading,
  Skeleton,
  TierStars,
} from "@/components/ui";
import { OwnershipSubnav } from "@/components/OwnershipSubnav";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";
import { useSession } from "@/session/SessionProvider";

type Listing = {
  listingId: string;
  horseId: string;
  horseName: string;
  tier: string;
  starRating: string;
  archetype: string;
  appearance: Record<string, unknown>;
  sellerName: string;
  priceCents: number;
  feeBp: number;
  status: string;
  createdAt: string;
};

type ListingsResponse = { listings: Listing[] };

export default function MarketplacePage() {
  const { session, requestLogin } = useSession();
  const queryClient = useQueryClient();
  const [offerListing, setOfferListing] = useState<Listing | null>(null);
  const [offerDollars, setOfferDollars] = useState("0");
  const [listDollars, setListDollars] = useState("50");
  const horseToList = new URLSearchParams(window.location.search).get("horse");
  useRealtimeTopic("world.market");

  const listings = useQuery({
    queryKey: ["market", "listings"],
    queryFn: () => api.marketListings() as Promise<ListingsResponse>,
    refetchInterval: 20_000,
  });
  const mine = useQuery({
    queryKey: ["market", "mine", session?.playerId],
    queryFn: () =>
      api.myMarket() as Promise<{
        listings: Listing[];
        offersMade: Array<Record<string, unknown>>;
        offersReceived: Array<Record<string, unknown>>;
      }>,
    enabled: Boolean(session),
  });
  const listingHorse = useQuery({
    queryKey: ["horses", horseToList],
    queryFn: () => api.horse(horseToList!),
    enabled: Boolean(session && horseToList),
  });
  const buy = useMutation({
    mutationFn: (listingId: string) => api.buyListing(listingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["market"] });
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
      void queryClient.invalidateQueries({ queryKey: ["balance"] });
    },
  });
  const offer = useMutation({
    mutationFn: () =>
      api.makeOffer(offerListing!.listingId, {
        cashCents: Math.round(Number(offerDollars) * 100),
        offeredHorseIds: [],
      }),
    onSuccess: () => {
      setOfferListing(null);
      void queryClient.invalidateQueries({ queryKey: ["market"] });
    },
  });
  const createListing = useMutation({
    mutationFn: () =>
      api.listHorse(horseToList!, Math.round(Number(listDollars) * 100)),
    onSuccess: () => {
      window.history.replaceState({}, "", "/app/marketplace");
      void queryClient.invalidateQueries({ queryKey: ["market"] });
    },
  });

  return (
    <div className="page page--marketplace">
      <OwnershipSubnav />
      <SectionHeading
        icon={<Store aria-hidden />}
        title="Horse Marketplace"
        action={
          <span className="muted-copy">
            Player-to-player exits preserve the treasury; buyback remains the
            floor.
          </span>
        }
      />

      {session && horseToList ? (
        <Panel className="market-listing-form">
          {listingHorse.error ? (
            <div className="route-feedback">
              {listingHorse.data ? (
                <p className="form-note">Showing the last loaded horse.</p>
              ) : null}
              <ErrorState
                error={listingHorse.error}
                retry={() => void listingHorse.refetch()}
              />
            </div>
          ) : null}
          {listingHorse.isPending ? <Skeleton rows={1} /> : null}
          <strong>
            List{" "}
            {String(
              (listingHorse.data as { name?: string } | undefined)?.name ??
                "horse",
            )}
          </strong>
          <label>
            Asking price
            <span>
              $
              <input
                min="1"
                onChange={(event) => setListDollars(event.target.value)}
                step="1"
                type="number"
                value={listDollars}
              />
            </span>
          </label>
          <Button
            disabled={
              createListing.isPending ||
              !listingHorse.data ||
              Number(listDollars) <= 0
            }
            onClick={() => createListing.mutate()}
          >
            {createListing.isPending ? "Creating listing…" : "Create listing"}
          </Button>
          {createListing.error ? (
            <p className="form-error route-feedback" role="alert">
              Listing not confirmed: {createListing.error.message}
            </p>
          ) : null}
        </Panel>
      ) : null}

      {buy.error ? (
        <Panel className="route-feedback">
          <p className="form-error" role="alert">
            Purchase not confirmed: {buy.error.message}
          </p>
        </Panel>
      ) : null}
      {offer.error && !offerListing ? (
        <Panel className="route-feedback">
          <p className="form-error" role="alert">
            Offer not confirmed: {offer.error.message}
          </p>
        </Panel>
      ) : null}
      {listings.error ? (
        <Panel aria-label="Marketplace listings">
          {listings.data ? (
            <p className="form-note">Showing the last loaded listings.</p>
          ) : null}
          <ErrorState
            error={listings.error}
            retry={() => void listings.refetch()}
          />
        </Panel>
      ) : null}
      {listings.isPending ? (
        <Skeleton variant="market-grid" />
      ) : listings.data?.listings.length ? (
        <div className="market-grid">
          {listings.data.listings.map((listing) => (
            <Panel className="market-card" key={listing.listingId}>
              <HorsePortrait
                horse={{
                  horseId: listing.horseId,
                  name: listing.horseName,
                  tier: listing.tier,
                  appearance: listing.appearance,
                  starRating: listing.starRating,
                }}
              />
              <div className="market-card__body">
                <strong>{listing.horseName}</strong>
                <TierStars rating={listing.starRating} tier={listing.tier} />
                <span>
                  {listing.archetype} · seller {listing.sellerName}
                </span>
                <b>{money(listing.priceCents)}</b>
              </div>
              <div className="market-card__actions">
                <Button
                  disabled={buy.isPending}
                  onClick={() =>
                    session ? buy.mutate(listing.listingId) : requestLogin()
                  }
                >
                  {buy.isPending && buy.variables === listing.listingId
                    ? "Buying…"
                    : "Buy"}
                </Button>
                <Button
                  onClick={() =>
                    session ? setOfferListing(listing) : requestLogin()
                  }
                  variant="secondary"
                >
                  Offer
                </Button>
              </div>
            </Panel>
          ))}
        </div>
      ) : listings.data ? (
        <EmptyState
          detail="Owners have not listed a horse yet."
          title="The marketplace is quiet"
        />
      ) : null}

      {session ? (
        <>
          <SectionHeading
            icon={<ArrowLeftRight aria-hidden />}
            title="My activity"
          />
          <Panel
            className="market-activity"
            aria-label="My marketplace activity"
          >
            {mine.error ? (
              <div className="route-feedback">
                {mine.data ? (
                  <p className="form-note">Showing the last loaded activity.</p>
                ) : null}
                <ErrorState
                  error={mine.error}
                  retry={() => void mine.refetch()}
                />
              </div>
            ) : null}
            {mine.isPending ? (
              <div className="route-feedback">
                <Skeleton rows={1} />
              </div>
            ) : null}
            {mine.data ? (
              <>
                <div>
                  <strong>{mine.data.listings.length}</strong>
                  <span>Listings</span>
                </div>
                <div>
                  <strong>{mine.data.offersMade.length}</strong>
                  <span>Offers made</span>
                </div>
                <div>
                  <strong>{mine.data.offersReceived.length}</strong>
                  <span>Offers received</span>
                </div>
                <Link href="/app/player">Manage from stable</Link>
              </>
            ) : null}
          </Panel>
        </>
      ) : null}

      {offerListing ? (
        <div
          className="modal-backdrop"
          onMouseDown={() => setOfferListing(null)}
          role="presentation"
        >
          <Panel
            className="modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2>Offer on {offerListing.horseName}</h2>
            <label>
              Cash offer
              <input
                min="0"
                onChange={(event) => setOfferDollars(event.target.value)}
                step="1"
                type="number"
                value={offerDollars}
              />
            </label>
            <p>
              Whole-horse swaps are supported by the backend; cash-only is the
              first destination flow.
            </p>
            {offer.error ? (
              <p className="form-error" role="alert">
                Offer not confirmed: {offer.error.message}
              </p>
            ) : null}
            <div className="modal__actions">
              <Button onClick={() => setOfferListing(null)} variant="ghost">
                Close offer
              </Button>
              <Button
                disabled={offer.isPending || Number(offerDollars) <= 0}
                onClick={() => offer.mutate()}
              >
                {offer.isPending ? "Submitting offer…" : "Submit offer"}
              </Button>
            </div>
          </Panel>
        </div>
      ) : null}
    </div>
  );
}
